/**
 * Hooks de administración de roles internos del portal.
 *
 * Un rol agrupa apps (qué ve) y se asigna a usuarios (quién lo tiene). Las
 * mutaciones envían CSRF automáticamente (lo añade `api/client`) e invalidan las
 * queries afectadas — incluida `['apps']`, porque cambiar roles altera qué apps
 * ve la gente.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiError } from '@/api/client';

export interface RoleSummary {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  soft_deleted_at: string | null;
  app_count: number;
  user_count: number;
}

export interface RoleAppEntry {
  app_id: number;
  slug: string;
  name: string;
}

export interface AdminUser {
  id: number;
  entra_object_id: string;
  display_name: string;
  email: string | null;
  department: string | null;
  office_location: string | null;
  last_login_at: string | null;
}

/** Usuario del directorio de Entra ID (TODOS, hayan entrado al portal o no). */
export interface DirectoryUser {
  entra_object_id: string;
  display_name: string;
  email: string | null;
  department: string | null;
  office_location: string | null;
  job_title: string | null;
  account_enabled: boolean;
  user_type: string | null;
  local_user_id: number | null;
  has_logged_in: boolean;
}

/** Se puede asignar por id local (ya entró) o por object id de Entra (cualquiera). */
export type AssignUserInput = { user_id: number } | { entra_object_id: string };

export interface DepartmentSummary {
  department: string | null;
  user_count: number;
}

export interface BulkAssignResult {
  assigned: number;
  already: number;
  total: number;
}

export interface CreateRolePayload {
  slug: string;
  name: string;
  description?: string | null;
}

const ROLES_KEY = ['admin', 'roles'] as const;

export function useAdminRoles(includeDeleted = false) {
  return useQuery<RoleSummary[], ApiError>({
    queryKey: [...ROLES_KEY, { includeDeleted }],
    queryFn: () =>
      api<RoleSummary[]>(
        `/api/admin/roles${includeDeleted ? '?include_deleted=true' : ''}`,
      ),
  });
}

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation<RoleSummary, ApiError, CreateRolePayload>({
    mutationFn: (payload) =>
      api<RoleSummary>('/api/admin/roles', { method: 'POST', body: payload }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROLES_KEY });
    },
  });
}

export function useSoftDeleteRole() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, number>({
    mutationFn: (roleId) =>
      api<void>(`/api/admin/roles/${roleId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROLES_KEY });
      void qc.invalidateQueries({ queryKey: ['apps'] });
    },
  });
}

// ── Apps del rol ────────────────────────────────────────────────────

export function useRoleApps(roleId: number | null) {
  return useQuery<RoleAppEntry[], ApiError>({
    queryKey: ['admin', 'roles', roleId, 'apps'],
    queryFn: () => api<RoleAppEntry[]>(`/api/admin/roles/${roleId}/apps`),
    enabled: roleId !== null,
  });
}

export function useSetRoleApps(roleId: number) {
  const qc = useQueryClient();
  return useMutation<RoleAppEntry[], ApiError, number[]>({
    mutationFn: (appIds) =>
      api<RoleAppEntry[]>(`/api/admin/roles/${roleId}/apps`, {
        method: 'PUT',
        body: { app_ids: appIds },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'roles', roleId, 'apps'] });
      void qc.invalidateQueries({ queryKey: ROLES_KEY });
      void qc.invalidateQueries({ queryKey: ['apps'] });
    },
  });
}

// ── Usuarios del rol ────────────────────────────────────────────────

export function useRoleUsers(roleId: number | null) {
  return useQuery<AdminUser[], ApiError>({
    queryKey: ['admin', 'roles', roleId, 'users'],
    queryFn: () => api<AdminUser[]>(`/api/admin/roles/${roleId}/users`),
    enabled: roleId !== null,
  });
}

export function useAssignUser(roleId: number) {
  const qc = useQueryClient();
  return useMutation<AdminUser, ApiError, AssignUserInput>({
    mutationFn: (input) =>
      api<AdminUser>(`/api/admin/roles/${roleId}/users`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'roles', roleId, 'users'] });
      void qc.invalidateQueries({ queryKey: ROLES_KEY });
      void qc.invalidateQueries({ queryKey: ['apps'] });
    },
  });
}

/** Asigna el rol a TODOS los usuarios de un departamento (`null` = sin departamento). */
export function useAssignByDepartment(roleId: number) {
  const qc = useQueryClient();
  return useMutation<BulkAssignResult, ApiError, string | null>({
    mutationFn: (department) =>
      api<BulkAssignResult>(`/api/admin/roles/${roleId}/users/by-department`, {
        method: 'POST',
        body: { department },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'roles', roleId, 'users'] });
      void qc.invalidateQueries({ queryKey: ROLES_KEY });
      void qc.invalidateQueries({ queryKey: ['apps'] });
    },
  });
}

export function useUnassignUser(roleId: number) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, number>({
    mutationFn: (userId) =>
      api<void>(`/api/admin/roles/${roleId}/users/${userId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'roles', roleId, 'users'] });
      void qc.invalidateQueries({ queryKey: ROLES_KEY });
      void qc.invalidateQueries({ queryKey: ['apps'] });
    },
  });
}

// ── Directorio de usuarios (para elegir a quién asignar) ─────────────

export function useAdminUsers(query = '', department: string | null = null) {
  return useQuery<AdminUser[], ApiError>({
    queryKey: ['admin', 'users', { query, department }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (query) params.set('q', query);
      if (department) params.set('department', department);
      const qs = params.toString();
      return api<AdminUser[]>(`/api/admin/users${qs ? `?${qs}` : ''}`);
    },
  });
}

/**
 * TODOS los usuarios de Entra ID (no solo los que han entrado al portal).
 * Requiere el permiso de aplicación User.Read.All concedido en la App Registration.
 */
export function useDirectoryUsers(query = '', department: string | null = null) {
  return useQuery<DirectoryUser[], ApiError>({
    queryKey: ['admin', 'users', 'directory', { query, department }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (query) params.set('q', query);
      if (department) params.set('department', department);
      const qs = params.toString();
      return api<DirectoryUser[]>(`/api/admin/users/directory${qs ? `?${qs}` : ''}`);
    },
  });
}

/** Departamentos (categorías) presentes en el directorio + nº de usuarios. */
export function useDepartments() {
  return useQuery<DepartmentSummary[], ApiError>({
    queryKey: ['admin', 'users', 'departments'],
    queryFn: () => api<DepartmentSummary[]>('/api/admin/users/departments'),
  });
}
